import throttle from 'lodash/throttle';
import { useEffect, useRef, useState } from 'react';

import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { PageLayoutActions } from 'layout/state/pageLayoutReducer';
import { useBanner, usePageLayoutDispatch, useWindowSize } from 'shared/hooks';

/**
 * [useToggleHeaderOnScrollEvent]
 * listens for scroll events, and
 * when scrolling down, hides the header
 * when scrolling up displays the header again
 */

export const useToggleHeaderOnScrollEvent = (isHomepage = false, preventToggle = false) => {
  const dispatch = usePageLayoutDispatch();
  const prevScrollY = useRef(0);
  const [scrollingDown, setScrollingDown] = useState(true);
  const { isBannerEnabled, title, subtitle } = useBanner();
  const { isMobile } = useWindowSize();
  const isBannerVisible = isBannerEnabled && (title || subtitle);
  const HEADER_HEIGHT = Number.parseInt(NEW_NAV_MOBILE_HEADER_HEIGHT, 10);
  const BANNER_HEIGHT = Number.parseInt(MOBILE_BANNER_HEIGHT, 10);
  const height = isBannerVisible ? HEADER_HEIGHT + BANNER_HEIGHT : HEADER_HEIGHT;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;

      if (isHomepage && isMobile) return;
      if (preventToggle) return;

      if (
        prevScrollY.current < currentScrollY &&
        Math.abs(prevScrollY.current - currentScrollY) > height &&
        scrollingDown
      ) {
        dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: false });
        setScrollingDown(false);
      }

      if (prevScrollY.current > currentScrollY && !scrollingDown) {
        dispatch({ type: PageLayoutActions.setIsHeaderVisible, payload: true });
        setScrollingDown(true);
      }

      prevScrollY.current = currentScrollY;
    };

    const throttledScrollHandler = throttle(handleScroll, 100);

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [scrollingDown, isMobile, preventToggle]);
};
